import { Item } from "./Item";
import { S25ItemI } from "./S25ItemI";
import { Proto } from "./Proto";
import { Event } from "./Event";
import Workflow = Event.Workflow.Task;
import ISODateString = Proto.ISODateString;
import { S25WsWrapperMicroServices } from "./S25WsNode";
import { Nullable } from "./Util";

export namespace Task {
    import NumericalString = Proto.NumericalString;
    export interface Data {
        subject: "task";
        id: Item.Ids.Task;
    }
    export interface Object {
        taskId: number;
        taskType: WorkflowType;
        taskTypeId?: Task.Id; //This could vary by contact so make it optional, recommend only use when it is global
        kind?: "task" | "todo";
        todoType?: Task.Todo.Type;
        taskName: string;
        allApprovalRequired?: boolean;
        eventId?: number;
        eventName?: string;
        profileId?: number; //ap only
        reservationId?: number;
        object?: S25ItemI; //space/resource to be approved - optional on todos
        overallState: State;
        comment: string;
        dueDate: Date;
        contacts: Contact[]; //todos only have one contact
        isBlocked?: boolean;
        blockingTasks?: BlockingTask[];

        success?: boolean; //Used to store success of micro service
        error?: string; //Used to store failure of micro service

        taskSubType?: Task.SubTypes; //Only on todos
        updated?: Date;
    }

    export interface BlockingTask {
        blockPriority: number; //The tier this task is on???
        blockPosition: number; //Number of tiers to be cleared before blocking is done
        blockingTaskType:
            | "space assignment"
            | "resource assignment"
            | "event type approval"
            | "organization approval"
            | "space approval"
            | "resource approval";
        blockingTaskId: number;
    }

    export interface wsBlocked {
        block_priority: number; //The tier this task is on???
        block_position: number; //Number of tiers to be cleared before blocking is done
        blocking_task_id: number;
        blocking_task_type:
            | "space assignment"
            | "resource assignment"
            | "event type approval"
            | "organization approval"
            | "space approval"
            | "resource approval";
    }

    export interface Contact {
        notifyType: Id; //What kind of task, contact specific to support FYI vs approval notification policy
        contId: number;
        contName: string;
        read: boolean;
        state: State;
        assignedById?: number;
        assignedByName?: string;

        success?: string;
        error?: string;
    }

    export type Id = (typeof Ids)[keyof typeof Ids];
    export type IdName = keyof typeof Ids;
    export const enum Ids {
        FYI = 1,
        Authorization = 2,
        Assign = 3,
        UnAssign = 4,
        Todo = 5,
        vCalTodo = 6,
    }

    export type WorkflowType = WorkflowTypes.notifyPolicy | WorkflowTypes.assignPolicy | WorkflowTypes.todo;
    export const enum WorkflowTypes {
        notifyPolicy = 1,
        assignPolicy = 2,
        todo = 3, //
    }

    export const enum SubTypes {
        cancel = 99,
        workflow = 1000,
    }

    export type StateName =
        | "Acknowledged"
        | "Approved"
        | "Assigned"
        | "Cancelled"
        | "Completed"
        | "Declined"
        | "Denied"
        | "Ignored"
        | "In Progress"
        | "Granted"
        | "Requested Changes"
        | "UnAssigned"
        | "Various";

    export type State =
        | States.InProgress
        | States.Completed
        | States.Granted
        | States.Declined
        | States.Denied
        | States.Cancelled
        | States.Ignored
        | States.Assigned
        | States.Acknowledged
        | States.RequestedChanges
        | States.Various;
    export const enum States {
        Various = -1,
        InProgress = 1,
        Completed = 2,
        Granted = 2,
        Assigned = 2,
        Acknowledged = 2,
        Denied = 3,
        Declined = 3,
        RequestedChanges = 4,
        Ignored = 5,
        Cancelled = 99,
    }

    export namespace State {
        const IconLookup: { [state in State]?: string } = {
            [States.InProgress]: "active",
            [States.Completed]: "affirmative",
            [States.Denied]: "negative",
            [States.Cancelled]: "completed",
        };

        export function iconClass(state: Nullable<Task.State>): string {
            if (!state) return "icon-task-unknown";
            return "icon-task-" + (IconLookup[state] ?? "unknown");
        }
    }

    export namespace Todo {
        export type Type =
            | Types.Private
            | Types.Public
            | Types.Import
            | Types.NeedsSpace
            | Types.HeadCountAdj
            | Types.EventModify;
        export const enum Types {
            Private = 1,
            Public = 2,
            Import = 100,
            NeedsSpace = 101,
            HeadCountAdj = 102,
            EventModify = 103,
        }

        export type Object = {
            taskName: string;
            dueDate: Date;
            assignedToId: number;
            assignedById: number;
            taskComment: string;
            subType?: SubTypes;
            eventId?: number;
        };
    }

    export type action = Actions.approve | Actions.deny | Actions.ignore | Actions.reset | Actions.cancel;
    // export type action = keyof typeof Actions;
    export const enum Actions {
        approve = "approve",
        deny = "deny",
        ignore = "ignore",
        reset = "reset",
        cancel = "cancel",
    }

    export interface PayloadI {
        content: {
            apiVersion: "0.1";
            etags?: string; //eg. "00000024"
            data: [{ tasks: WsDataI[] }];
        };
    }

    export interface WsDataI {
        taskIdList?: number[];
        todoIdList?: number[];
        action?: Actions;
        readState?: "read" | "unread";
        rsvpDate?: string; //ISO8601,
        assignContact?: { contactId: number }[];
        unassignContact?: { contactId: number }[];
        assignGroup?: { groupId: number }[];
        unassignGroup?: { groupId: number }[];
        comments?: string;
        etag?: string;
    }

    export interface TaskListI {
        actions?: TaskListActions;
        assignedTo?: TaskListAssignTo;
        comments?: TaskListComments;
        eventDate?: Date;
        flagged?: TaskListFlagged;
        reference?: string;
        respondBy?: TaskListRespondBy;
        status?: TaskListStatus;
        taskItem?: TaskListTaskItem;
        type?: string;
        workflowChained?: boolean;
        blockTask?: [];
        currentUserId?: number;
        hasApproveDenyPerms?: boolean;
        objectType?: number;
        objectId?: number;
        taskBlocked?: boolean | string;
        readOnly?: boolean;
        competes?: { spaceCompetes: SpaceCompete[] };
        eventName?: string;
    }

    export interface TaskListStatus {
        itemName: string;
        itemStateId: Task.State;
    }

    export interface TaskListComments {
        templateType: number;
        value: string;
        isTodo?: boolean;
        taskId: number;
        eventId?: number;
        assignedToId: number;
        assignedById: number;
        requesterId: number;
    }
    export interface TaskListRespondBy {
        templateType: number;
        date: Date | "";
        taskId: number;
        taskCount?: number;
        eventId?: number;
        assignedToId: number;
        assignedById: number;
        requesterId: number;
        isTodo?: boolean;
    }
    export interface TaskListTaskItem {
        eventId: number;
        templateType?: number; //deprecated with TS migration
        itemId: number;
        itemName: string;
        itemTypeId: number;
        itemStateId: number; //pass user state to taskItem bc it passes it to modal to drive actions there...
        itemCount?: number;
        itemQuantity?: number;
        isTodo?: boolean;
        objectType?: number;
        objectId?: number;
        requesterId: number;
        contacts?: Contact[];
        todoType?: Task.Todo.Type;
        todoSubType?: Task.SubTypes;
        taskId?: number;
        state?: Task.State;
        competes?: SpaceCompete[];
        eventName?: string;
        reservationId?: number | null;
    }

    export interface TaskListFlagged {
        templateType: number;
        isTodo?: boolean;
        assignedToId: number;
        assignedById: number;
        requesterId: number;
        active: boolean;
        value?: boolean | string;
        label: string;
        action: Function;
    }

    export interface TaskListAssignTo {
        itemName: string;
        itemId?: number;
        task?: Workflow;
        eventId?: number;
        templateType?: number;
    }

    export interface TaskListActions {
        templateType: number;
        itemId: number;
        itemStateId: number;
        itemTypeId: number;
        itemCount: number;
        eventId: number;
        taskName: string;
        objectType: number;
        assignedToId: number;
        assignedById: number;
        requesterId: number;
        todoType?: number;
        todoSubType?: number;
        isTodo?: boolean;
        taskBlocked?: boolean | string; //CharBoolean
        workflowChained?: boolean;
    }

    //Typed response of tasks.json
    export interface wsTask {
        assigned_by_name: string;
        task_name: string;
        assigned_to_id: number;
        task_id: NumericalString;
        task_type_name: string;
        scheduler_id: number;
        request_quantity: number;
        blocking_task: wsBlocked[];
        task_count: number;
        reference: string;
        related_data: number;
        assigned_to: string;
        assigned_by_id: number;
        read: string;
        event_state: string;
        comments: string;
        object_type: number;
        object_name: string;
        task_state: number;
        event_title: string;
        head_count: string;
        creation_date: Date;
        object_id: number;
        task_state_name: string;
        event_favorite: string;
        event_state_id: number;
        event_id: number;
        task_blocked: string;
        event_name: string;
        assigned_by: string;
        scheduler_name: string;
        task_type: NumericalString;
        task_subtype: number;
        respond_by: Date;
        first_date: Date;
    }

    export interface TodoWs {
        assigned_by_name: string;
        type_name: string;
        todo_subtype: Task.SubTypes;
        assigned_to_id: number;
        todo_id: number;
        cur_state_id: number;
        priority_id: number;
        crc: string;
        cur_state_name: string;
        object_name: string;
        id: string;
        assigned_by_id: number;
        read: string;
        object_type: string;
        last_mod_dt: Date;
        type_id: number;
        due_date: Date;
        completed: string;
        priority: string;
        comment_id: number;
        object_id: number;
        version: number;
        additional_info: string;
        last_mod_user: string;
        name: string;
        comment: string;
        object_type_name: string;
        assigned_to_name: string;
        status: string;
    }

    //typed response of PUT to micro/task/list.json
    export interface MicroTaskWsWrapper extends S25WsWrapperMicroServices {
        data: { items: MicroTaskWs[] };
    }

    // https://knowledge25.knowledgeowl.com/help/tasksxml
    export interface MicroParams {
        query_id?: string;
        outstanding?: Proto.CharBoolean;
        unread?: Proto.CharBoolean;
        due_start_dt?: Proto.ISODateString;
        due_end_dt?: Proto.ISODateString;
        task_type?: Task.Id[];
        task_state?: Task.State[];
        task_id?: number[];
        space_id?: number;
        resource_id?: number;
        event_id?: number[];
        event_type_id?: number;
        assigned_to?: number;
        assigned_by?: number;
        scope?: "count" | "list" | "extended";
        include?: MicroScopes[];
        user_id?: number;
    }

    export type MicroScopes = "contacts" | "competes" | "blocking" | "reservations";

    export interface MicroTaskWs {
        kind: "task" | "todo";
        id: number; //taskId
        etag?: string;
        updated: ISODateString;
        taskCount: number;
        taskState: Task.States;
        taskType: Task.Id;
        taskTypeName: keyof typeof Task.Ids;
        rsvpDt: ISODateString;
        eventId: number;
        profileId?: number;
        reservationId: number;
        objectId: number;
        objectType: number;
        creationDate: ISODateString;
        approvalFrom: string;
        approvalContacts?: ApprovalContact[]; //not on todos
        currentContact?: ApprovalContact;
        blockingTasks?: string;
        active: boolean;
        assignedById?: number; //todo only
        assignedToId?: number; //todo only
        readState?: boolean; //todo only
        competes?: Compete[]; //for waitlist
    }

    export interface Compete {
        spaceCompetes: SpaceCompete[];
    }
    export interface SpaceCompete {
        spaceId: number;
        conflictType?: string;
        conflictId: number; //eventId
        conflictRsrvId: number; //approval_profile_id / reservationId
        conflictName: string;
        conflictPreEventDt?: ISODateString;
        conflictRsrvStartDt?: ISODateString;
        conflictEvStartDt?: ISODateString;
        conflictEvEndDt?: ISODateString;
        conflictRsrvEndDt?: ISODateString;
        conflictPostEventDt?: ISODateString;
    }

    export interface ApprovalContact {
        contactId: number;
        notifyType: number;
        readState: boolean;
        currentNotifyState: number;
        trackChanges: boolean;
    }

    export interface DBTodo {
        todoId: number;
        todoName: string;
        todoSubtype?: number;
        curTodoState?: number;
        curAssignedTo?: number;
        relObjectId?: number;
        relObjectType?: number;
        commentId?: number;
        comment?: TextComment;
        compDt?: string;
        lastModDt?: string;
        lastModUser?: string;
    }

    export interface TextComment {
        commentId: number;
        commentText: string;
        lastModDt: string;
        uVersion: string;
        lastModUser: string;
    }
}

//Response from a successful TaskService.changeEventTaskState
// interface approvalResp {
//     alertText: "",
//     error: undefined,
//     item: {
//
//     },
//     overallState: 1,
//     success: {
//         success: "success",
//         contacts: [{
//             "approval_contact_state_name": "Granted/Completed",
//             "approval_contact_name": "Athletic Director",
//             "read_state": "F",
//             "crc": "00000040",
//             "approval_contact_state": 2,
//             "notification_type_id": 2,
//             "approval_contact_id": 67,
//             "read_state_name": "unread",
//             "status": "est",
//             "notification_type_name": "Authorization",
//             "approval_history": [
//                 {
//                     "history_contact_id": 67,
//                     "history_notify_state": 1,
//                     "history_contact_name": "Athletic Director",
//                     "history_date": "2022-09-25T22:45:39-04:00",
//                     "history_notify_state_name": "In Progress"
//                 }
//             ]
//         }]
//     }
// }
